import React, { useState } from 'react';
import { Send, Mail, Phone, MapPin, User } from 'lucide-react';
import siteConfig from '../config/siteConfig';
import { sendEmails } from '../services/emailService';
import './ContactForm.css';

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Send both user confirmation and client notification emails
            const results = await sendEmails(formData);

            if (results.userEmailSent && results.clientEmailSent) {
                alert('Thank you! Your message has been sent. We will contact you soon.');
                setFormData({ name: '', email: '', phone: '', company: '', address: '', message: '' });
            } else if (results.userEmailSent || results.clientEmailSent) {
                alert('Thank you for reaching out! We will get back to you soon.');
                console.warn('Partial email delivery:', results.errors);
            } else {
                alert('There was an issue sending your message. Please try again or contact us directly.');
                console.error('Email sending failed:', results.errors);
            }
        } catch (error) {
            alert('There was an error submitting your request. Please try again later.');
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="section contact-form-section">
            <div className="container">
                <div className="contact-wrapper">
                    {/* Contact Info */}
                    <div className="contact-info">
                        <h2>Get in Touch</h2>
                        <p>Have a question or ready to start your export journey? We're here to help.</p>

                        <div className="contact-details">
                            <div className="contact-detail-item">
                                <div className="contact-detail-icon">
                                    <Mail size={24} />
                                </div>
                                <div>
                                    <h4>Email Us</h4>
                                    <a href={`mailto:${siteConfig.contact.email}`}>
                                        {siteConfig.contact.email}
                                    </a>
                                </div>
                            </div>

                            <div className="contact-detail-item">
                                <div className="contact-detail-icon">
                                    <Phone size={24} />
                                </div>
                                <div>
                                    <h4>Call Us</h4>
                                    <a href={`tel:${siteConfig.contact.phone}`}>
                                        {siteConfig.contact.phone}
                                    </a>
                                </div>
                            </div>

                            <div className="contact-detail-item">
                                <div className="contact-detail-icon">
                                    <MapPin size={24} />
                                </div>
                                <div>
                                    <h4>Office Address</h4>
                                    <p>
                                        {siteConfig.contact.address.street}<br />
                                        {siteConfig.contact.address.city}, {siteConfig.contact.address.state}<br />
                                        {siteConfig.contact.address.country} - {siteConfig.contact.address.pincode}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="contact-form-container">
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-group">
                                <label htmlFor="name">Full Name *</label>
                                <div className="input-wrapper">
                                    <User size={18} />
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        placeholder="John Doe"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address *</label>
                                <div className="input-wrapper">
                                    <Mail size={18} />
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        placeholder="john@example.com"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone">Phone Number</label>
                                <div className="input-wrapper">
                                    <Phone size={18} />
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="+91 79873 52105"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="company">Company Name</label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    placeholder="Your Company"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="address">Address/Location</label>
                                <div className="input-wrapper">
                                    <MapPin size={18} />
                                    <input
                                        type="text"
                                        id="address"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        placeholder="City, State, Country"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="message">Message *</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows="5"
                                    placeholder="Tell us about your requirements..."
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                className="btn btn-gold btn-lg"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                                <Send size={20} />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactForm;
